import { ApiError, type ResumeValue, type RunEvent, type RunView } from "@procurement-agents/scope-api";
import { useCallback, useEffect, useRef, useState } from "react";
import { useScopeApi } from "./api";
import { applyEvent, EMPTY_OVERLAY, emptyStore, settle, type Overlay, type StreamStore } from "./stream";

const BACKOFF = [600, 800, 1000, 1500, 2000, 3000];
const STREAM_RETRY = [1000, 2000, 4000, 8000, 10000];
/** Something is happening on the run: the pipeline runs, the editing agent works, or a section is being re-checked. */
const isBusy = (r: RunView | null) => !!r && (r.status === "running" || !!(r as { agent_busy?: boolean }).agent_busy || ((r as { rechecking?: string[] }).rechecking?.length ?? 0) > 0);

/**
 * Watches a run. While it is `running` the live event stream (GET /runs/{id}/events) drives the store and polling
 * is paused; if the stream is unavailable, closes early or errors, polling GET /runs/{id} takes over (fast right
 * after a send, backing off to 3 s) and the stream is retried with backoff. Token text lives in `overlay`, a
 * display-only layer discarded once state settles, so a dropped stream never leaves stale text.
 */
export function useRun(runId: string, pollMs = 3000, { stream = true }: { stream?: boolean } = {}) {
  const { api, toast } = useScopeApi();
  const [store, setStore] = useState<StreamStore>(() => emptyStore());
  const storeRef = useRef(store); storeRef.current = store;
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | null>(null);
  const polling = useRef(false);
  const connected = useRef(false);       // SSE open
  const abort = useRef<AbortController | null>(null);
  const retryTimer = useRef<number | null>(null);
  const retries = useRef(0);
  const alive = useRef(true);

  const commit = useCallback((fn: (s: StreamStore) => StreamStore) => { storeRef.current = fn(storeRef.current); setStore(storeRef.current); }, []);
  const fail = useCallback((e: unknown) => setError(e instanceof ApiError ? e : new ApiError(0, String(e))), []);

  const fetchState = useCallback(async (): Promise<RunView | null> => {
    try { const r = await api.getRun(runId); commit((s) => settle(s, r)); setError(null); return r; } catch (e) { fail(e); return null; }
  }, [api, runId, commit, fail]);

  /** Poll with backoff from step n until the run is no longer running or the stream takes over. */
  const schedule = useCallback((n: number) => {
    polling.current = true;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      if (connected.current || !alive.current) { polling.current = false; return; }
      const r = await fetchState();
      if (isBusy(r) && !connected.current) schedule(n + 1); else polling.current = false;
    }, BACKOFF[Math.min(n, BACKOFF.length - 1)] ?? pollMs);
  }, [fetchState, pollMs]);

  /** Open the stream; on close re-fetch state (the server closes after a terminal status); on error fall back to polling and retry. */
  const openStream = useCallback(async () => {
    if (!stream || connected.current || !alive.current) return;
    const ctl = new AbortController(); abort.current = ctl; connected.current = true;
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; polling.current = false; }
    const onEvent = (ev: RunEvent) => {
      if (ctl.signal.aborted) return;
      commit((s) => applyEvent(s, ev));
      // a side job finished while the pipeline is paused: state (findings, sections, busy flags) is re-fetched now,
      // whether or not the server closes the stream afterwards
      if ((ev.type === "chat_turn" || ev.type === "recheck" || ev.type === "suggestion") && ev.data.status !== "started") void fetchState();
      // an artifact landed mid-step (the requisition while market research runs): show it now, not at the step's end
      if (ev.type === "artifact") void fetchState();
    };
    let outcome: "closed" | "aborted" | "error" = "error";
    try { outcome = await api.streamEvents(runId, { signal: ctl.signal, lastEventId: storeRef.current.lastId, onEvent }); } catch { outcome = ctl.signal.aborted ? "aborted" : "error"; }
    connected.current = false;
    if (outcome === "aborted" || !alive.current) return;
    if (outcome === "closed") retries.current = 0;
    const r = await fetchState();                      // state is the source of truth after any close
    if (isBusy(r)) {
      if (!polling.current) schedule(0);
      const delay = STREAM_RETRY[Math.min(retries.current++, STREAM_RETRY.length - 1)]!;
      retryTimer.current = window.setTimeout(() => { void openStream(); }, delay);
    }
  }, [api, runId, stream, commit, fetchState, schedule]);

  const watch = useCallback((r: RunView | null) => { if (isBusy(r)) { if (stream) void openStream(); else if (!polling.current) schedule(0); } }, [stream, openStream, schedule]);

  const refresh = useCallback(async () => { const r = await fetchState(); if (!connected.current) watch(r); return r; }, [fetchState, watch]);

  useEffect(() => {
    alive.current = true;
    commit(() => emptyStore());
    void refresh();
    return () => {
      alive.current = false;
      if (timer.current) window.clearTimeout(timer.current);
      if (retryTimer.current) window.clearTimeout(retryTimer.current);
      abort.current?.abort(); connected.current = false; polling.current = false;
    };
  }, [refresh, commit]);

  const resume = useCallback(async (value: ResumeValue) => {
    setBusy(true);
    try {
      await api.resume(runId, value);
      commit((s) => ({ ...s, overlay: EMPTY_OVERLAY, run: s.run ? { ...s.run, status: "running", next: null, current_node: null, progress: null } as RunView : s.run }));  // flip to running immediately
      watch(storeRef.current.run);
      // Then read it again. An answer can change who is driving the run, and with it the shape of the view the
      // server sends: choosing to carry on at the hand-over, or handing over part-way. The flip above keeps the old
      // shape, so without this a requester who chose to continue was told "the procurement team has this now"
      // until the next step finished.
      void refresh();
    } catch (e) {
      // a refusal with a reason (take over first, not yours to change) says it; a bare 409 is a race with someone else
      if (e instanceof ApiError && e.status === 409 && !e.code) { toast("Someone else is answering this, or a step is in progress.", "warn"); await refresh(); }
      else if (e instanceof ApiError) toast(e.detail, "warn");   // shown, never swallowed: the button would otherwise just do nothing
      else throw e;
    } finally { setBusy(false); }
  }, [api, runId, refresh, watch, commit, toast]);

  const retry = useCallback(async () => { await api.retry(runId); await refresh(); }, [api, runId, refresh]);
  /** After a chat turn or re-check was enqueued: flag the agent busy and start streaming its events. */
  const agentStarted = useCallback(() => {
    commit((s) => ({ ...s, run: s.run ? ({ ...s.run, agent_busy: true } as RunView) : s.run }));
    watch(storeRef.current.run);
  }, [commit, watch]);
  const overlay: Overlay = store.overlay;
  return { run: store.run, overlay, error, busy, resume, retry, refresh, agentStarted };
}
